import { cn } from "@/utils";

interface LoadingSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "primary" | "secondary" | "subtle" | "white";
  speed?: "slow" | "normal" | "fast";
}

export const LoadingSpinner = ({
  size = "md",
  variant = "primary",
  className,
  speed = "normal",
  ...props
}: LoadingSpinnerProps) => {
  // Size configurations
  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };

  const borderWidths = {
    xs: "border-2",
    sm: "border-2",
    md: "border-3",
    lg: "border-4",
    xl: "border-4",
  };

  // Speed configurations
  const speedClasses = {
    slow: "2s",
    normal: "1s",
    fast: "0.6s",
  };

  // Variant configurations using design tokens
  const variants = {
    primary: {
      background: "var(--color-border-secondary)",
      foreground: "var(--color-primary)",
      foregroundFaded: "color-mix(in srgb, var(--color-primary) 19%, transparent)",
    },
    secondary: {
      background: "var(--color-bg-tertiary)",
      foreground: "var(--color-secondary)",
      foregroundFaded: "color-mix(in srgb, var(--color-secondary) 19%, transparent)",
    },
    subtle: {
      background: "var(--color-bg-secondary)",
      foreground: "var(--color-text-tertiary)",
      foregroundFaded: "color-mix(in srgb, var(--color-text-tertiary) 19%, transparent)",
    },
    white: {
      background: "color-mix(in srgb, var(--color-white) 20%, transparent)",
      foreground: "var(--color-white)",
      foregroundFaded: "color-mix(in srgb, var(--color-white) 19%, transparent)",
    },
  };

  const currentVariant = variants[variant];

  return (
    <div className={cn("inline-flex items-center justify-center", className)} {...props}>
      <div
        className={cn(
          sizeClasses[size],
          borderWidths[size],
          "rounded-full border-transparent animate-spin",
        )}
        style={{
          borderTopColor: currentVariant.foreground,
          borderRightColor: currentVariant.foregroundFaded,
          borderBottomColor: currentVariant.background,
          borderLeftColor: currentVariant.background,
          animationDuration: speedClasses[speed],
          animationTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        role="status"
        aria-label="Loading"
      />
    </div>
  );
};
